/**
 * hooks.ts — Data fetching otimizado para Agentes e Ambientes
 * 
 * Padrão Big Tech: Stale-While-Revalidate
 * - 1 único request: GET /cloud/workspaces/dashboard (machines + subscription + plan)
 * - Render instantâneo do cache localStorage
 * - Fetch em background sem loading spinner
 * - Skeleton só no primeiro load (sem cache)
 * - Polling inteligente: pausa quando tab oculta
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { api } from '../../api/client';
import type { Environment, Agent, Workspace } from './types';

const CACHE_KEY = 'agentes_dashboard_v2';
const POLL_INTERVAL = 30_000;
const POLL_FAST = 5_000;
const POLL_CREATING = 10_000;

interface DashboardCache {
  machines: Environment[];
  subscription: any;
  plan: any;
  token_usage?: any;
  ts: number;
}

function readCache(): DashboardCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function writeCache(data: Omit<DashboardCache, 'ts'>) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, ts: Date.now() }));
  } catch {}
}

/** Hook principal: carrega tudo em 1 request com SWR */
export function useEnvironments() {
  const cache = readCache();
  const [environments, setEnvironments] = useState<Environment[]>(cache?.machines || []);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(!cache); // Só mostra loading se NÃO tem cache
  const [userPlan, setUserPlan] = useState(
    cache?.subscription?.plan_id || cache?.subscription?.plan || 'free'
  );
  const [workspacesLimit, setWorkspacesLimit] = useState(
    cache?.plan?.workspaces_limit ?? 1
  );
  const [tokenUsage, setTokenUsage] = useState<{ input_tokens: number; output_tokens: number; total_tokens: number; requests: number } | null>(
    cache?.token_usage || null
  );
  const [restartingIds, setRestartingIds] = useState<Set<string>>(new Set());
  const [justCreated, setJustCreated] = useState(false);
  const [ready, setReady] = useState(!!cache); // Se tem cache, já está ready
  const intervalRef = useRef<any>(null);
  const visibleRef = useRef(true);

  const fetchData = useCallback(async () => {
    // Não buscar se tab oculta (exceto se ainda em loading)
    if (!visibleRef.current && !loading) return;

    try {
      // 1 ÚNICO REQUEST — tudo vem junto
      const res = await api.get<{
        machines: Environment[];
        subscription: any;
        plan: any;
        token_usage?: any;
      }>('/cloud/workspaces/dashboard');

      const envs = res.machines || [];

      // Preservar agentes quando ambiente está reiniciando
      setEnvironments(prev => {
        if (prev.length === 0) return envs;
        return envs.map(env => {
          const prevEnv = prev.find(p => p.id === env.id);
          if (prevEnv && (!env.agents || env.agents.length === 0) && prevEnv.agents && prevEnv.agents.length > 0) {
            return { ...env, agents: prevEnv.agents, agent_info: prevEnv.agent_info || env.agent_info };
          }
          return env;
        });
      });

      // Subscription + plan
      const slug = res.subscription?.plan_id || res.subscription?.plan || 'free';
      setUserPlan(slug.toLowerCase());
      const wLimit = res.plan?.workspaces_limit ?? 1;
      setWorkspacesLimit(typeof wLimit === 'number' ? wLimit : parseInt(wLimit, 10) || 1);

      // Token usage
      if (res.token_usage) setTokenUsage(res.token_usage);

      // Gravar cache
      writeCache({ machines: envs, subscription: res.subscription, plan: res.plan, token_usage: res.token_usage });
    } catch (err) {
      console.warn('[Dashboard] fetchData error:', err);
    } finally {
      setLoading(false);
      if (!ready) requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
    }
  }, [loading, ready]);

  // Tab visibility — pausar polling quando oculta
  useEffect(() => {
    const onVisibility = () => {
      visibleRef.current = !document.hidden;
      // Ao voltar visível, fetch imediato
      if (!document.hidden) fetchData();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [fetchData]);

  // Polling principal
  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  // Polling rápido quando criando
  useEffect(() => {
    const hasCreating = environments.some(e =>
      ['creating', 'provisioning', 'configuring_setup', 'installing_openclaw'].includes(e.status) ||
      e.type === 'pending_installation'
    );
    if (hasCreating) {
      const fast = setInterval(fetchData, POLL_CREATING);
      return () => clearInterval(fast);
    }
  }, [environments, fetchData]);

  // Polling rápido após criar
  useEffect(() => {
    if (!justCreated) return;
    const iv = setInterval(fetchData, POLL_FAST);
    const timeout = setTimeout(() => setJustCreated(false), 60000);
    return () => { clearInterval(iv); clearTimeout(timeout); };
  }, [justCreated, fetchData]);

  useEffect(() => {
    if (justCreated && environments.length > 0) setJustCreated(false);
  }, [justCreated, environments]);

  // Auto-limpar restarting quando volta online
  useEffect(() => {
    if (restartingIds.size === 0) return;
    restartingIds.forEach(id => {
      const env = environments.find(e => e.id === id);
      if (env && env.status === 'running') {
        setRestartingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      }
    });
  }, [environments, restartingIds]);

  return {
    environments, agents, loading, userPlan, workspacesLimit,
    restartingIds, setRestartingIds, justCreated, setJustCreated,
    ready, fetchData, tokenUsage,
  };
}
